/* global window, document */
import {domains, languages, fixedDomains} from './data/constants.json';


export const innerHTMLTimeout = 50;

export const getLocalizedLocation = (location = null, language = 'en') => {
  const alternateLink = typeof document !== 'undefined' && document.querySelector(`link[rel="alternate"][hreflang="${language}"]`);
  const alternateHref = alternateLink && alternateLink.getAttribute('href');
  const protocol = location && location.protocol ? location.protocol : 'https:';
  const hostname = location && location.hostname;
  const domain = fixedDomains.indexOf(hostname) >= 0 ? hostname : `${language === 'en' ? 'www' : language}.${domains.wixNaked}`;
  const path = location ? `${location.pathname || ''}${location.search || ''}` : '';
  return alternateHref || `${protocol}//${domain}${path}`;
};

export const setLanguageUrl = (language, location, {languageRedirectUrl = null}) => {
  const defaultRedirectUrl = getLocalizedLocation(location, language);
  const modifiedRedirectUrl = languageRedirectUrl ? languageRedirectUrl({language}) : null;
  const redirectToURL = typeof modifiedRedirectUrl === 'string' ? modifiedRedirectUrl : defaultRedirectUrl;
  return `https://${domains.wixUsers}/wix-users/auth/setLanguage?language=${language}&redirectTo=${encodeURIComponent(redirectToURL)}`;
};

const createMobileLoginLink = ({language = 'en'}) => language === 'en' ? `https://${domains.wixUsers}/signin` : `https://${domains.wixUsers}/signin?overrideLocale=${language}`;
const createDesktopLoginLink = ({postLogin, postSignUp, language}) => {
  const desktopTabletParams = {
    referralInfo: 'HEADER',
    loginDialogContext: 'login',
    referralAdditionalInfo: 'header',
    postLogin,
    postSignUp,
  };

  const getParamsString = Object.keys(desktopTabletParams)
    .filter(param => desktopTabletParams[param])
    .map(param => [param, encodeURIComponent(desktopTabletParams[param])].join('='))
    .join('&');

  if (language === 'en') {
    return `https://${domains.wixUsers}/signin?${getParamsString}`;
  } else {
    return `https://${domains.wixUsers}/signin?${getParamsString}&overrideLocale=${language}`;
  }
};

export const loginUrl = (config, isMobile) => isMobile ? createMobileLoginLink(config) : createDesktopLoginLink(config);

export const languagesMenu = (list = [], location = null, config = {}, fallback = 'en') =>
  list.map(language => {
    const lang = languages[language] ? language : fallback;

    return {
      text: languages[lang].origin,
      href: setLanguageUrl(language, location, config),
      contentAttrs: {
        'data-bi-name': languages[lang].origin,
        'aria-label': languages[lang].eng,
        'data-hook': `language-link-${lang}`,
      }
    };
  });

export const obtainLanguage = (fallback = 'en') => {
  if (typeof window === 'undefined') {
    return fallback;
  }
  const subDomain = location.host.split('.')[0];
  if (subDomain !== 'www' && subDomain.length === 2) {
    return subDomain;
  }
  return document.cookie.indexOf('wixLanguage=') >= 0 ?
    document.cookie.substr(document.cookie.indexOf('wixLanguage=') + 12, 2) :
    fallback;
};

export const getValueFromObjectByPrefix = (object, prefix = '') => {
  const objectClosure = Object.assign({}, object);
  return key => objectClosure[`${prefix}${key}`] || '';
};

export const linkObjectFromMap = (menuItemData, translations, forLanguage = 'en', modifiers = []) => {
  const translate = getValueFromObjectByPrefix(translations);
  const getLink = getValueFromObjectByPrefix(translations, 'link_');

  let {text, href, target, rel, title, important, lang, tab, name} = menuItemData;

  if (href && href.indexOf('/') === 0 && href.indexOf('//') !== 0) {
    href = `https://${forLanguage === 'en' ? domains.wix : `${forLanguage}.${domains.wixNaked}`}${href}`;
  }

  let linkObject = {
    dataHook: text,
    text: translate(text),
    href: getLink(text) || href,
    target,
    name,
    rel,
    subs: [],
    title: translate(title) || href || text,
    important: !!important,
    lang,
    tab,
  };

  modifiers.forEach(modifier => {
    linkObject = modifier(linkObject, forLanguage);
    if (linkObject === null) {
      return linkObject;
    }
  });
  return linkObject;
};

export const menuFromData = (menuData, translations, forLanguage = 'en', modifiers = []) => {
  return menuData.map(item => {
    const obj = linkObjectFromMap(item, translations, forLanguage, modifiers);

    if (item.subs && obj) {
      obj.subs = menuFromData(item.subs, translations, forLanguage, modifiers);
    }

    return obj;
  }).filter(Boolean);
};

export const createNodeInBody = (tagName = 'div', insertBefore = null, id = null, classNames = []) => {
  const node = document.createElement(tagName);
  if (id) {
    node.setAttribute('id', id);
  }
  if (classNames && classNames.length) {
    node.setAttribute('class', classNames.join(' '));
  }
  document.body.insertBefore(node, insertBefore);
  return node;
};

export const getRootNodes = (headerSelector, footerSelector) => {
  const nodes = {
    header: headerSelector ? document.querySelector(headerSelector) : null,
    footer: footerSelector ? document.querySelector(footerSelector) : null,
  };
  if (!nodes.header) {
    nodes.header = createNodeInBody('nav', document.body.firstChild);
  }
  if (!nodes.footer) {
    nodes.footer = createNodeInBody('footer', null);
  }
  return nodes;
};

const moveHeaderNode = node => {
  const newRootNode = document.createElement('header');
  node.replaceWith(newRootNode);
  document.body.insertBefore(newRootNode, document.body.firstChild);
  return newRootNode;
};

export const sanitizeRootNodes = (nodes = {}, config = {}) => {
  const defaults = {
    header: {
      selector: '#wix-menu',
      tag: 'nav',
      place: document.body.firstChild,
    },
    footer: {
      selector: '#wix-footer',
      tag: 'footer',
      place: null,
    },
  };
  const parts = config.useOnly ? [config.useOnly] : ['header', 'footer'];

  parts.forEach(part => {
    if (!(nodes[part] instanceof HTMLElement)) {
      const {selector, tag, place} = defaults[part];
      nodes[part] = document.querySelector(selector) || createNodeInBody(tag, place);
    }
  });
  if (config.accessibilityChanges || config.hostPage === 'editor') {
    if (nodes.header) {
      nodes.header = moveHeaderNode(nodes.header);
    }
  }
};

export const mapObject = (object, mapper) => {
  const mappedObject = {};
  Object.keys(object).forEach(key => mappedObject[key] = mapper(object[key], key));
  return mappedObject;
};

export const innerHTML = (node, html, className, lang = null, setAttrs = {}) => { // eslint-disable-line
  node.innerHTML = '';
  node.removeAttribute('id');
  if (lang) {
    node.setAttribute('lang', lang);
  }
  if (className) {
    node.setAttribute('class', className);
  }
  Object.keys(setAttrs).forEach(attr => node.setAttribute(attr, setAttrs[attr]));

  setTimeout(() => {
    node.innerHTML = html || '';
  }, innerHTMLTimeout);
};

export const arrayWithoutDublicates = origin =>
  origin.filter((item, position) => origin.indexOf(item) === position);

export const getClassesFromString = string =>
  string ? arrayWithoutDublicates(string.split(' ')) : [];

export const toggleClass = (node, string, force = null) => {
  const classNames = getClassesFromString(node.className);
  const givenClassNames = getClassesFromString(string);
  givenClassNames.forEach(className => {
    const index = classNames.indexOf(className);
    if ((force === true || force === null) && index < 0) {
      classNames.push(className);
    } else if ((force === false || force === null) && index >= 0) {
      classNames.splice(index, 1);
    }
  });
  node.className = classNames.join(' ');
};

export const hasClass = (node, string) =>
  getClassesFromString(node.className).indexOf(string) >= 0;

export const truncate = (string, length) =>
  string.length <= length ? string : string.slice(0, length) + '...';

export const toCamelCase = str =>
  str.toLowerCase()
    .replace(/[-_]+/g, ' ')
    .replace(/[^\w\s]/g, '')
    .replace(/ (.)/g, match => match.toUpperCase())
    .replace(/ /g, '');

export const wrapLinkWithBiEvent = (logger, link, evid, extendParams) => {
  const listener = event => {
    event.preventDefault();
    const params = {evid};


    if (extendParams) {
      Object.assign(params, extendParams(link));
    }

    const referencedWindow = link.target === '_blank' ? window.open() : window;
    logger.log(params).then(() => {
      referencedWindow && referencedWindow.location.assign(link.href);
    });
  };
  link.addEventListener('click', listener);
  link.addEventListener('tap', listener);
};

export const parseURI = string => {
  const link = document.createElement('a');
  link.href = string;
  return link;
};

export const enlist = (...args) =>
  args.reduce((result, arg) => result.concat(arg), []);

export const ensure = (value, args = []) =>
  value === undefined ? null : value instanceof Function ? value.apply(null, args) : value;

export const intersection = (...arrays) =>
  arrays.length === 0 ? [] :
    arrays.length === 1 ? arrayWithoutDublicates(arrays[0]) :
      arrays.length === 2 ? arrayWithoutDublicates(arrays[0]).filter(element => arrays[1].indexOf(element) >= 0) :
        arrays.slice(1).reduce((result, array) => arrayWithoutDublicates(result).filter(element => array.indexOf(element) >= 0), arrays[0]);

export const throttle = (fn, threshhold = 250, context = this) => {
  let last, deferTimer;
  return function () {
    const now = Date.now();
    const args = arguments;
    if (last && now < last + threshhold) {
      clearTimeout(deferTimer);
      deferTimer = setTimeout(() => {
        last = now;
        fn.apply(context, args);
      }, threshhold);
    } else {
      last = now;
      fn.apply(context, args);
    }
  };
};

export const clone = value =>
  value === null ? null :
    typeof value === 'symbol' ? Symbol('cloned') :
      typeof value === 'object' ? JSON.parse(JSON.stringify(value)) :
        typeof value === 'function' ? value.bind({}) :
          value;

export const capitalize = value =>
  value.charAt(0).toUpperCase() + value.substr(1);

export const warnAndReturn = (message, returnValue, printPromiseErrors = true) => (...errors) => {
  const toPrint = printPromiseErrors ? [message].concat(errors) : [message];
  console.warn(...toPrint);
  return returnValue;
};

export const warnAndThen = (...warns) =>
  new Promise(() => console.warn(...warns));

export const isInt = value =>
  typeof value === 'number' ? parseInt(value) === value : false;

export const parentWithClass = (node, classname) =>
  !node ? null :
    hasClass(node, classname) ? node :
      parentWithClass(node.parentNode, classname);

export const setAttributes = (node, attributes) =>
  Object.keys(attributes)
    .forEach(attribute => node.setAttribute(attribute, attributes[attribute]));

export const removeAttributes = (node, attributes) =>
  Object.keys(attributes)
    .forEach(attribute => node.removeAttribute(attribute));

export const $ = (query, context = document) =>
  context.querySelector(query);

export const $$ = (query, context = document) =>
  Array.from(context.querySelectorAll(query));

export const getLoadTimeParam = () => {
  if (window.W && window.W.Site && window.W.Site.Common && window.W.Site.Common.HeaderInitTime) {
    return {load_time: Date.now() - window.W.Site.Common.HeaderInitTime}; // eslint-disable-line
  }
};

/**
 * @returns null | {[key: string]: string[]}
 */
export const getUrlQuery = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  const queryStr = window.location.search.substr(1);
  if (!queryStr) {
    return {};
  }
  return queryStr.split('&').reduce((acc, keyVal) => {
    const [key, val] = keyVal.split('=');
    if (!(key in acc)) {
      acc[key] = [];
    }
    val && acc[key].push(val);
    return acc;
  }, {});
};

export const shouldHideHeaderAndFooter = () => {
  const query = getUrlQuery();
  if (!query) {
    return true;
  }
  return 'hideheaderandfooter' in query;
};

export const addNewLanguagesToLanguagePicker = (config, experiments) => {
  if (experiments['specs.funnel.EnableNewLanguagesInWixHeader'] === 'true' && experiments['specs.fw.ExperimentalLanguagesToggle_languages']) {
    const langsArr = experiments['specs.fw.ExperimentalLanguagesToggle_languages'].split(',');
    langsArr.forEach(newLanguage => {
      const lang = newLanguage.trim();
      !config.supportedLanguages.includes(lang) && config.supportedLanguages.push(lang);
    });
  }
};
